import { useState, useEffect } from 'react';
import { AuthenticationResult } from 'src/core/services/authentication';
import { container } from '../inversify.config';
import PermissionService from './permission-service';

export function usePermissionCheck(userId: string, scopeId: string, permission: string): [boolean, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    svc
      .check(userId, scopeId, permission)
      .then((result) => {
        setHasPermission(result);
        setIsLoading(false);
      })
      .catch((_) => {
        setHasPermission(false);
        setIsLoading(false);
      });
  }, [userId, scopeId, permission]); // eslint-disable-line react-hooks/exhaustive-deps

  return [hasPermission, isLoading];
}

export function useCurrentUserPermissionCheck(
  identity: AuthenticationResult | undefined | null,
  scopeId: string,
  permission: string
): [boolean, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const userId = identity?.profile['sub'];

  useEffect(() => {
    setIsLoading(true);
    if (userId == null) {
      setHasPermission(false);
      setIsLoading(false);
    } else {
      svc
        .check(userId, scopeId, permission)
        .then((result) => {
          setHasPermission(result);
          setIsLoading(false);
        })
        .catch((_) => {
          setHasPermission(false);
          setIsLoading(false);
        });
    }
  }, [userId, scopeId, permission]); // eslint-disable-line react-hooks/exhaustive-deps

  return [hasPermission, isLoading];
}

export function useCurrentUserTenantPermissionCheck(identity: AuthenticationResult | undefined | null, permission: string): [boolean, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const userId = identity?.profile['sub'];
  const tenantId = identity?.profile['tenant_id'];

  useEffect(() => {
    setIsLoading(true);
    if (userId == null || tenantId == null) {
      setHasPermission(false);
      setIsLoading(false);
    } else {
      svc
        .check(userId, tenantId, permission)
        .then((result) => {
          setHasPermission(result);
          setIsLoading(false);
        })
        .catch((_) => {
          setHasPermission(false);
          setIsLoading(false);
        });
    }
  }, [userId, tenantId, permission]); // eslint-disable-line react-hooks/exhaustive-deps

  return [hasPermission, isLoading];
}

export function usePermissionsFilter(userId: string, scopeId: string, permissions: string[]): [Set<string>, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [filteredPermissions, setFilteredPermissions] = useState<Set<string>>(new Set<string>([]));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    svc
      .filter(userId, scopeId, permissions)
      .then((result) => {
        setFilteredPermissions(new Set<string>(result || []));
        setIsLoading(false);
      })
      .catch((_) => {
        setFilteredPermissions(new Set<string>([]));
        setIsLoading(false);
      });
  }, [userId, scopeId, permissions]); // eslint-disable-line react-hooks/exhaustive-deps

  return [filteredPermissions, isLoading];
}

export function useCurrentUserPermissionsFilter(
  identity: AuthenticationResult | undefined | null,
  scopeId: string,
  permissions: string[]
): [Set<string>, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [filteredPermissions, setFilteredPermissions] = useState<Set<string>>(new Set<string>([]));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const userId = identity?.profile['sub'];

  useEffect(() => {
    setIsLoading(true);
    if (userId == null) {
      setFilteredPermissions(new Set<string>([]));
      setIsLoading(false);
    } else {
      svc
        .filter(userId, scopeId, permissions)
        .then((result) => {
          setFilteredPermissions(new Set<string>(result || []));
          setIsLoading(false);
        })
        .catch((_) => {
          setFilteredPermissions(new Set<string>([]));
          setIsLoading(false);
        });
    }
  }, [userId, scopeId, permissions]); // eslint-disable-line react-hooks/exhaustive-deps

  return [filteredPermissions, isLoading];
}

export function useCurrentUserTenantPermissionsFilter(
  identity: AuthenticationResult | undefined | null,
  permissions: string[]
): [Set<string>, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [filteredPermissionsSet, setFilteredPermissionsSet] = useState<Set<string>>(new Set<string>([]));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const userId = identity?.profile['sub'];
  const tenantId = identity?.profile['tenant_id'];

  useEffect(() => {
    setIsLoading(true);
    if (userId == null || tenantId == null) {
      setFilteredPermissionsSet(new Set<string>([]));
      setIsLoading(false);
    } else {
      svc
        .filter(userId, tenantId, permissions)
        .then((result) => {
          setFilteredPermissionsSet(new Set<string>(result || []));
          setIsLoading(false);
        })
        .catch((_) => {
          setFilteredPermissionsSet(new Set<string>([]));
          setIsLoading(false);
        });
    }
  }, [userId, tenantId, permissions]); // eslint-disable-line react-hooks/exhaustive-deps

  return [filteredPermissionsSet, isLoading];
}

export function useFilterEntitiesByPermission(userId: string, entitiesId: string[], permission: string): [Set<string>, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [filteredEntities, setFilteredEntities] = useState<Set<string>>(new Set<string>([]));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    svc
      .filterScopes(userId, entitiesId, permission)
      .then((result) => {
        setFilteredEntities(new Set<string>(result || []));
        setIsLoading(false);
      })
      .catch((_) => {
        setFilteredEntities(new Set<string>([]));
        setIsLoading(false);
      });
  }, [userId, entitiesId, permission]); // eslint-disable-line react-hooks/exhaustive-deps

  return [filteredEntities, isLoading];
}

export function useCurentUserFilterEntitiesByPermission(
  identity: AuthenticationResult | undefined | null,
  entitiesId: string[],
  permission: string
): [Set<string>, boolean] {
  const svc: PermissionService = container.get(PermissionService);
  const [filteredEntities, setFilteredEntities] = useState<Set<string>>(new Set<string>([]));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const userId = identity?.profile['sub'];

  useEffect(() => {
    setIsLoading(true);
    if (userId == null) {
      setFilteredEntities(new Set<string>([]));
      setIsLoading(false);
    } else {
      svc
        .filterScopes(userId, entitiesId, permission)
        .then((result) => {
          setFilteredEntities(new Set<string>(result || []));
          setIsLoading(false);
        })
        .catch((_) => {
          setFilteredEntities(new Set<string>([]));
          setIsLoading(false);
        });
    }
  }, [userId, entitiesId, permission]); // eslint-disable-line react-hooks/exhaustive-deps

  return [filteredEntities, isLoading];
}
